<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import LiteYouTubeEmbed from 'vue-lite-youtube-embed';
import { vueVimeoPlayer } from 'vue-vimeo-player';

import { LanguageTranslation, Locales } from '@/store/language.interface';
import { getLocale, getTranslation } from '@/utils/data-display-utils';

// PROPS
const props = defineProps<{
  type: string;
  title?: LanguageTranslation;
  videoId: LanguageTranslation;
  languageEnabled?: Locales[];

  /**
   * When the caller controls the aspect ratio (e.g. Tailwind `aspect-video`),
   * Vimeo's internal responsive wrapper should be disabled to avoid layout shifts.
   */
  vimeoResponsive?: boolean;
}>();

// TYPES
type ImageResolution = 'default' | 'maxresdefault' | 'sddefault' | 'hqdefault' | 'mqdefault';

// REFS
const poster = ref<ImageResolution>('default');
const locale = computed(() => getLocale(props.languageEnabled));

// COMPUTED
const isYoutube = computed(() => props.type === 'Youtube');
const isVimeo = computed(() => !isYoutube.value);
const effectiveVimeoResponsive = computed(() => props.vimeoResponsive ?? false);

// LIFECYCLE
onMounted(async () => {
  if (isYoutube.value) {
    poster.value = await getPoster();
  }
});

// FUNCTIONS
async function getPoster(): Promise<ImageResolution> {
  if (!props.languageEnabled) return 'default';
  const img = new Image();
  const videoId = getTranslation(props, 'videoId', props.languageEnabled);
  const resolutions = ['maxresdefault', 'sddefault', 'hqdefault'] as ImageResolution[];
  for (const resolution of resolutions) {
    const url = `https://i.ytimg.com/vi/${videoId}/${resolution}.jpg`;
    const loaded = await new Promise<boolean>((resolve) => {
      img.onload = () => resolve(img.naturalWidth > 120);
      img.onerror = () => resolve(false);
      img.src = url;
    });
    if (loaded) return resolution;
  }
  return 'default';
}

// WATCH
watch(locale, async () => {
  if (isYoutube.value) {
    poster.value = await getPoster();
  }
});
</script>

<template>
  <div class="video-player h-full w-full">
    <div v-if="isYoutube" class="h-full w-full">
      <LiteYouTubeEmbed
        :id="getTranslation(props, 'videoId', props.languageEnabled)"
        :title="getTranslation(props, 'title', props.languageEnabled)"
        :poster="poster"
      />
    </div>

    <div v-else class="vimeo-fill h-full w-full">
      <vue-vimeo-player
        ref="player"
        :video-id="getTranslation(props, 'videoId', props.languageEnabled)"
        :options="{ responsive: effectiveVimeoResponsive }"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.video-player {
  width: 100%;
  height: 100%;
}

.video-player.is-background {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* kill lite-youtube’s ratio box */
.video-player.is-background :deep(article.yt-lite) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0 !important;
  background-size: cover;
  background-position: center;
}

/* TRUE "object-cover" behavior without vh/vw */
.video-player.is-background :deep(article.yt-lite iframe) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  /* scale up to cover (16:9 video inside arbitrary container) */
  transform: scale(1.25);

  border: 0;
}

/* Hide the big red play overlay (strong + specific) */
.video-player.is-background ::v-deep(article.yt-lite > button.lty-playbtn) {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

/* Safety: if their markup changes slightly */
.video-player.is-background ::v-deep(button.lty-playbtn) {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

.vimeo-fill {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
}

.video-player.is-background .vimeo-fill {
  height: 100%;
  aspect-ratio: auto;
}

.vimeo-fill > * {
  width: 100%;
  height: 100%;
}

.vimeo-fill :deep(iframe) {
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  border: 0;
}
</style>
